/**
 * Real-time progressive data capture, the foundational primitive for
 * Lembra's "second order for Sarah will be easy" experience.
 *
 * Why this exists:
 *   Customers gift to a specific recipient ~once a year. We can't
 *   wait for repeat orders to learn. The aha-moment must land on
 *   order 2, which means order 1's data must be fully captured,
 *   even when the user abandons mid-flow. This module is the
 *   client-side write path for that.
 *
 * Where the data lives:
 *   public.interaction_captures (Supabase). See migration
 *   20260513060000 for schema + rationale.
 *
 * Split with PostHog:
 *   This module captures PERSISTENT state (typed fields, drafted
 *   briefs, expressed preferences) that we query at runtime to power
 *   features. PostHog continues to handle BEHAVIOUR TELEMETRY (clicks,
 *   scrolls, hovers), different tool for different jobs. Don't
 *   double-instrument; use PostHog for analytics, this module for
 *   feature-powering data.
 *
 * Conventions:
 *   - Capture is best-effort. Never throw from this module, capturing
 *     should never break the customer's flow. All errors swallowed
 *     after a console.warn.
 *   - For high-frequency events (typing), use captureDebounced() with
 *     a key per field (e.g. "chat_brief_input"), prevents one row per
 *     keystroke.
 *   - For one-off events (button click, brief drafted, sku viewed),
 *     use captureInteraction() directly.
 *   - Set recipient_id when the interaction is about a specific team
 *     member; set vendor_product_id when about a specific SKU. These
 *     unlock the "what did the customer think about Sarah?" and
 *     "which SKUs get viewed but never bought?" queries.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";

// Surface enum mirrors the DB CHECK constraint
// (interaction_captures_surface_check). When you add a new surface,
// add it here AND alter the DB constraint.
//
// MS146: added order_confirm, product_detail, landing. These were
// already being passed from OrderConfirmSheet / ProductDetailModal /
// InlineConcierge — but the DB constraint rejected them silently
// (captureInteraction logs the error and swallows). The migration
// 20260517020000 extends the constraint to allow them. Without
// strict TS this mismatch went undetected for months.
export type InteractionSurface =
  | "smart_input"
  | "chat"
  | "browse"
  | "wizard"
  | "profile"
  | "order"
  | "approval"
  | "autopilot"
  | "order_confirm"
  | "product_detail"
  | "landing"
  | "dashboard";

export interface CaptureParams {
  /** Which UI surface this came from. */
  surface: InteractionSurface;
  /** Free-form event type within the surface. Document the taxonomy
   *  in the surface's component file (e.g. ChatWidget.tsx header). */
  event_type: string;
  /** JSONB payload. Shape depends on event_type. Defaults to {}. */
  payload?: Record<string, unknown>;
  /** Page route. Defaults to window.location.pathname when not set. */
  surface_route?: string;
  /** When the interaction is about a specific team member, set this.
   *  Powers second-order personalisation queries. */
  recipient_id?: string | null;
  /** When the interaction is about a specific SKU, set this. Powers
   *  content-strategy "viewed-but-not-bought" queries. */
  vendor_product_id?: string | null;
  /** Authenticated user id. The hook below auto-injects via auth.uid()
   *  if you don't pass it. */
  user_id?: string | null;
  /** Company id when known. */
  company_id?: string | null;
}

const SESSION_KEY = "lembra_session_id";

/**
 * Lazily generate + persist a session id in localStorage. Used to tie
 * together anonymous interactions across page loads + to bridge to
 * authenticated identity when the user later signs up.
 *
 * Returns "ssr" during server-side rendering, those captures are
 * dropped (caller checks for window). The first client-side capture
 * after hydration creates the real session id.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID?.() ?? `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Insert one interaction_captures row. Best-effort, errors are
 * logged but never thrown. Safe to call from anywhere in the
 * customer-facing code without a try/catch.
 */
export async function captureInteraction(params: CaptureParams): Promise<void> {
  // No-op during SSR, there's no session id worth capturing yet.
  if (typeof window === "undefined") return;

  try {
    const session_id = getOrCreateSessionId();
    const surface_route =
      params.surface_route ??
      (typeof window !== "undefined" ? window.location.pathname : null);

    const row: Database["public"]["Tables"]["interaction_captures"]["Insert"] = {
      session_id,
      surface: params.surface,
      surface_route,
      event_type: params.event_type,
      payload: (params.payload ?? {}) as Json,
      recipient_id: params.recipient_id ?? null,
      vendor_product_id: params.vendor_product_id ?? null,
      user_id: params.user_id ?? null,
      company_id: params.company_id ?? null,
    };

    const { error } = await supabase.from("interaction_captures").insert(row);

    if (error) {
      // Don't throw, this module is non-critical. Log so we can
      // catch real issues during development without breaking the
      // user's flow in production.
      console.warn("[captureInteraction] DB insert failed:", error.message);
    }
  } catch (err) {
    console.warn("[captureInteraction] threw:", err);
  }
}

// ─── Debounced variant for high-frequency events (typing) ──────────
//
// Use case: capture the chat brief as the user types, but don't write
// one row per keystroke. captureDebounced("chat_brief", { ... }, 500)
// fires only after 500ms of no further calls under the same key.
//
// Each surface should pick stable keys per field (e.g.
// "chat_brief_input", "profile_hobbies_field", "smart_input_main")
// so debounces don't collide across surfaces.

const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function captureDebounced(
  key: string,
  params: CaptureParams,
  delayMs = 500,
): void {
  const existing = debounceTimers.get(key);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    captureInteraction(params);
    debounceTimers.delete(key);
  }, delayMs);

  debounceTimers.set(key, timer);
}

/**
 * Force-flush a pending debounced capture (e.g. when the user
 * navigates away or submits the form). Ensures we don't lose the
 * last partial capture before page transition.
 */
export function captureFlush(key: string): void {
  const existing = debounceTimers.get(key);
  if (existing) {
    clearTimeout(existing);
    debounceTimers.delete(key);
    // The debounced capture is gone, caller is expected to do a
    // direct captureInteraction() call with the final state if they
    // want it persisted.
  }
}

// ─── Convenience: capture from form-input change events ────────────
//
// Wrap your <input onChange={...}> handler to auto-capture every
// debounced change. Common shape:
//
//   <input
//     value={email}
//     onChange={(e) => {
//       setEmail(e.target.value);
//       captureFieldChange("smart_input", "email", e.target.value);
//     }}
//   />

export function captureFieldChange(
  surface: InteractionSurface,
  fieldName: string,
  value: string | number | boolean | null,
  extraPayload: Record<string, unknown> = {},
): void {
  captureDebounced(
    `${surface}_${fieldName}`,
    {
      surface,
      event_type: "field_typed",
      payload: {
        field: fieldName,
        value,
        ...extraPayload,
      },
    },
    500,
  );
}
